import { InitialData } from '../../../config/load'
import { GetProduct } from '../../../types/product'
import { parseDriveFile } from '../../../utilities/fileTools'
import {
    storagePage,
    formInformation,
    formNorms,
    tableCompaniesControl,
    tablePermitsControl,
    tableProductsControl,
    tableTaxesControl,
    tableFiscalCodeControl
} from '../storage'
import { PRODUCT_TYPES } from '../constants'
import setParamsSearchProducts from './setParamsSearchProducts'
import applyTypeDetailsToForm from './applyTypeDetailsToForm'

export default function prepareForEdition(
    product: GetProduct & {
        equipment_details?: any
        consumable_details?: any
        service_details?: any
        country_availability?:
            | { ni: boolean; cr: boolean; pa: boolean; ve: boolean }
            | {
                  country_ids: number[]
                  countries: Array<{
                      id: number
                      name: string
                      code: string
                  }>
              }
        literature_files?: Array<{
            file_id: number
            file?: any
            sort_order?: number
        }>
    },
    countries: InitialData['countries']
) {
    const isNewProduct =
        (product.code && product.code.startsWith('NP-')) ||
        Boolean(
            product.equipment_details ||
                product.consumable_details ||
                product.service_details
        )

    const equipmentDetails = isNewProduct ? product.equipment_details : null
    const consumableDetails = isNewProduct ? product.consumable_details : null
    const serviceDetails = isNewProduct ? product.service_details : null
    const typeName = product.type_product?.name || ''
    const typeIndex = PRODUCT_TYPES.findIndex(
        type => type.toLowerCase() === typeName.toLowerCase()
    )
    const resolvedTypeIndex =
        typeIndex >= 0 ? typeIndex : Number(product.type_product?.id || 0)

    let selectedServiceIds: number[] = []
    let selectedCertificationIds: number[] = []
    if (isNewProduct) {
        if (resolvedTypeIndex === 0) {
            const serviceIdsFromNew =
                equipmentDetails?.service_ids || ([] as number[])
            const serviceIdLegacy = equipmentDetails?.service_id
                ? [equipmentDetails.service_id]
                : []
            selectedServiceIds =
                (serviceIdsFromNew || []).length > 0
                    ? serviceIdsFromNew
                    : serviceIdLegacy

            const certificationIdsFromNew =
                equipmentDetails?.certification_ids || ([] as number[])
            const certificationIdLegacy = equipmentDetails?.certification_id
                ? [equipmentDetails.certification_id]
                : []
            selectedCertificationIds =
                (certificationIdsFromNew || []).length > 0
                    ? certificationIdsFromNew
                    : certificationIdLegacy
        } else if (resolvedTypeIndex === 1) {
            const certificationIdsFromNew =
                consumableDetails?.certification_ids || ([] as number[])
            const certificationIdLegacy = consumableDetails?.certification_id
                ? [consumableDetails.certification_id]
                : []
            selectedCertificationIds =
                (certificationIdsFromNew || []).length > 0
                    ? certificationIdsFromNew
                    : certificationIdLegacy
        } else if (resolvedTypeIndex === 2) {
            const certificationIdsFromNew =
                serviceDetails?.certification_ids || ([] as number[])
            const certificationIdLegacy = serviceDetails?.certification_id
                ? [serviceDetails.certification_id]
                : []
            selectedCertificationIds =
                (certificationIdsFromNew || []).length > 0
                    ? certificationIdsFromNew
                    : certificationIdLegacy
        }
    }
    const countryAvailability = isNewProduct
        ? product.country_availability
        : null
    const literatureFiles = isNewProduct ? product.literature_files || [] : []

    const countryAvailOptions: Array<{ label: string; value: string }> = []
    if (countryAvailability) {
        const avail = countryAvailability as any
        if (avail.countries && Array.isArray(avail.countries)) {
            avail.countries.forEach(
                (c: { id: number; name: string; code: string }) => {
                    countryAvailOptions.push({
                        label: c.name || c.code || String(c.id),
                        value: String(c.id)
                    })
                }
            )
        } else if (avail.ni || avail.cr || avail.pa || avail.ve) {
            if (avail.ni) countryAvailOptions.push({ label: 'NI', value: '1' })
            if (avail.cr) countryAvailOptions.push({ label: 'CR', value: '2' })
            if (avail.pa) countryAvailOptions.push({ label: 'PA', value: '3' })
            if (avail.ve) countryAvailOptions.push({ label: 'VE', value: '4' })
        }
    }
    if (
        countryAvailOptions.length === 0 &&
        product.product_countries &&
        product.product_countries.length > 0
    ) {
        product.product_countries.forEach((pc: { country_id: number }) => {
            const country = countries.find(
                (c: { id: number }) => c.id === pc.country_id
            )
            if (country) {
                countryAvailOptions.push({
                    label: (country as any).name,
                    value: (country as any).id.toString()
                })
            }
        })
    }

    let photo: string | any = ''
    if (isNewProduct) {
        if (equipmentDetails?.photo_file_id) {
            photo = equipmentDetails.photo_file_id.toString()
        } else if (consumableDetails?.photo_file_id) {
            photo = consumableDetails.photo_file_id.toString()
        } else if (serviceDetails?.photo_file_id) {
            photo = serviceDetails.photo_file_id.toString()
        }
    } else if (product.files && product.files.length > 0) {
        photo = product.files[0].id?.toString() || ''
    }

    /**
     * Step 1
     */
    formInformation.init({
        name: product.name,
        manufacturer_code: isNewProduct
            ? equipmentDetails?.manufacturer_code ||
              consumableDetails?.manufacturer_code ||
              serviceDetails?.manufacturer_code ||
              ''
            : product.manufacturer_code,
        rank: product.rank || '',
        model: isNewProduct
            ? equipmentDetails?.equipment_model || ''
            : product.model || '',
        quantity: product.quantity || '',
        brand: {
            label: product.brand.name,
            value: product.brand.id?.toString() || ''
        },
        product_files: product.files
            ? product.files.map(f => parseDriveFile(f))
            : [],
        type_product: (() => {
            const typeName = product.type_product.name
            const typeIndex = PRODUCT_TYPES.findIndex(
                type => type.toLowerCase() === typeName.toLowerCase()
            )
            return {
                label: typeName,
                value:
                    typeIndex >= 0
                        ? typeIndex.toString()
                        : product.type_product.id.toString()
            }
        })(),
        type_communications: product.product_type_communications
            ? product.product_type_communications
                  .filter(ptc => ptc.memory)
                  .map(ptc => ({
                      label: ptc.type_communication.name,
                      value: ptc.type_communication.id.toString()
                  }))
            : [],
        hazardous: product.is_hazardous || false,
        precursor: product.is_precursor || false,
        description_generate: product.description,
        category: {
            label: product.subcategory.category.name,
            value: product.subcategory.category_id.toString()
        },
        subcategory: {
            label: product.subcategory.name,
            value: product.subcategory.id.toString()
        },
        code: product.code,
        measurement_range: isNewProduct
            ? equipmentDetails?.measurement_range || ''
            : '',
        country_availability: countryAvailOptions,
        impact_ecosystem: isNewProduct
            ? (
                  equipmentDetails?.impact_ecosystem_ids ||
                  consumableDetails?.impact_ecosystem_ids ||
                  []
              ).map((id: number) => ({ label: '', value: String(id) }))
            : [],
        productive_process: isNewProduct
            ? (
                  equipmentDetails?.productive_process_ids ||
                  consumableDetails?.productive_process_ids ||
                  []
              ).map((id: number) => ({ label: '', value: String(id) }))
            : [],
        vertical_market: isNewProduct
            ? (
                  equipmentDetails?.vertical_market_ids ||
                  consumableDetails?.vertical_market_ids ||
                  []
              ).map((id: number) => ({ label: '', value: String(id) }))
            : [],
        fiscal_code_ni: isNewProduct
            ? equipmentDetails?.fiscal_code_ni ||
              consumableDetails?.fiscal_code_ni ||
              serviceDetails?.fiscal_code_ni ||
              ''
            : '',
        fiscal_code_cr: isNewProduct
            ? equipmentDetails?.fiscal_code_cr ||
              consumableDetails?.fiscal_code_cr ||
              serviceDetails?.fiscal_code_cr ||
              ''
            : '',
        fiscal_code_pa: isNewProduct
            ? equipmentDetails?.fiscal_code_pa ||
              consumableDetails?.fiscal_code_pa ||
              serviceDetails?.fiscal_code_pa ||
              ''
            : '',
        fiscal_code_ve: isNewProduct
            ? equipmentDetails?.fiscal_code_ve ||
              consumableDetails?.fiscal_code_ve ||
              serviceDetails?.fiscal_code_ve ||
              ''
            : '',
        photo: photo,
        literature: literatureFiles
            .filter(lf => lf.file)
            .map(lf => parseDriveFile(lf.file)),
        services: selectedServiceIds.map(id => ({
            label: '',
            value: id.toString()
        })),
        certification: selectedCertificationIds.map(id => ({
            label: '',
            value: id.toString()
        })),
        composition: isNewProduct ? consumableDetails?.composition || '' : '',
        useful_life:
            isNewProduct && consumableDetails?.useful_life_id
                ? {
                      label: '',
                      value: consumableDetails.useful_life_id.toString()
                  }
                : null,
        certificate: isNewProduct
            ? serviceDetails?.certificate || false
            : false,
        packaging:
            isNewProduct && consumableDetails?.packaging_id
                ? {
                      label: '',
                      value: consumableDetails.packaging_id.toString()
                  }
                : null,
        presentation_quantity:
            isNewProduct && consumableDetails?.presentation_quantity
                ? consumableDetails.presentation_quantity.toString()
                : '',
        presentation_unit:
            isNewProduct && consumableDetails?.presentation_unit_id
                ? {
                      label: '',
                      value: consumableDetails.presentation_unit_id.toString()
                  }
                : null,
        air_transport: isNewProduct
            ? consumableDetails?.air_transport || false
            : false,
        maritime_transport: isNewProduct
            ? consumableDetails?.maritime_transport || false
            : false,
        refrigeration: isNewProduct
            ? consumableDetails?.refrigeration || false
            : product.refrigeration || false,
        regulation:
            isNewProduct && consumableDetails?.regulation_id
                ? {
                      label: '',
                      value: consumableDetails.regulation_id.toString()
                  }
                : null
    })

    if (!product.brand.is_enabled) {
        formInformation.setProps('brand', { disabled: true })
    } else {
        formInformation.setProps('brand', { disabled: false })
    }

    applyTypeDetailsToForm(resolvedTypeIndex)

    /**
     * Step 2
     */

    const min_temperature = product.min_temperature
        ? product.min_temperature.toString()
        : ''

    const max_temperature = product.max_temperature
        ? product.max_temperature.toString()
        : ''

    formNorms.init({
        lifetime: product.lifetime > 0 ? product.lifetime.toString() : '',
        refrigeration: product.refrigeration,
        min_temperature,
        max_temperature,
        air_transport: product.air_transport,
        marine_transport: product.marine_transport,
        ground_transport: true,
        storable_products: product.non_storable_products.map(nsprod => ({
            value: nsprod.id.toString(),
            label: nsprod.name
        }))
    })

    const nonStorabledProducts = product.non_storable_products.map(nsprod => ({
        id: nsprod.id,
        name: nsprod.name,
        code: nsprod.code,
        category_name: nsprod?.subcategory.category.name,
        subcategory_name: nsprod.subcategory.name
    }))

    tableProductsControl.setList(nonStorabledProducts)
    storagePage.products.sets(product.non_storable_products)
    setParamsSearchProducts()

    /**
     * Step 3
     */

    tableCompaniesControl.setList(
        product.product_countries
            .map(pc =>
                pc.product_companies.map(pcom => ({
                    company_id: pcom.company_id,
                    can_sell: pcom.can_sell,
                    can_buy: pcom.can_buy,
                    image: pcom.company.country.image_url,
                    company_name: pcom.company.name,
                    is_enabled: pcom.company.is_enabled
                }))
            )
            .flat()
    )

    tableFiscalCodeControl.setList(
        product.product_countries
            .map(pc =>
                pc.product_companies.map(pcom => ({
                    company_id: pcom.company_id,
                    country_image: pcom.company.country.image_url,
                    company_name: pcom.company.name,
                    is_enabled: pcom.company.is_enabled,
                    fiscal_code: pcom.fiscal_code || ''
                }))
            )
            .flat()
    )

    tableTaxesControl.setList(
        product.product_countries.map(product_country => {
            const country = countries.find(
                _country => product_country.country_id === _country.id
            )

            return {
                country_id: country.id,
                tax: (product_country.tax || '').toString(),
                image: country.image_url,
                country_name: country.name
            }
        })
    )

    tablePermitsControl.setList(
        product.product_countries.map(pc => {
            const country = countries.find(
                _country => pc.country_id === _country.id
            )

            return {
                country_id: country.id,
                nationalization_permission: pc.nationalization_permission,
                shipment_permission: pc.shipment_permission,
                ministry_health_permission: pc.ministry_health_permission,
                new_files: pc.files.map(f => parseDriveFile(f)),
                image: country.image_url,
                country_name: country.name
            }
        })
    )
}
